import { sectionRenderer } from '@theme/section-renderer';

class GWHeaderPredictiveSearch extends HTMLElement {
  #timer = null;
  #controller = null;
  #items = [];
  #selectedIndex = -1;

  connectedCallback() {
    this.input = this.querySelector('[data-gw-search-input]');
    this.results = this.querySelector('[data-gw-search-results]');
    this.form = this.querySelector('form');

    if (!this.input || !this.results || !this.form) return;

    this.minChars = Number(this.dataset.minChars || 2);
    this.limit = Math.min(10, Math.max(1, Number(this.dataset.limit || 8)));

    this.input.addEventListener('input', this.#onInput);
    this.input.addEventListener('keydown', this.#onKeyDown);
    this.input.addEventListener('focus', this.#onFocus);
    document.addEventListener('pointerdown', this.#onDocumentPointerDown);
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    this.#controller?.abort();
    this.input?.removeEventListener('input', this.#onInput);
    this.input?.removeEventListener('keydown', this.#onKeyDown);
    this.input?.removeEventListener('focus', this.#onFocus);
    document.removeEventListener('pointerdown', this.#onDocumentPointerDown);
  }

  #onInput = () => {
    clearTimeout(this.#timer);

    const query = this.input.value.trim();
    this.#selectedIndex = -1;
    this.input.removeAttribute('aria-activedescendant');
    this.#items.forEach((item) => {
      item.setAttribute('aria-selected', 'false');
      item.classList.remove('keyboard-focus');
    });

    if (query.length < this.minChars) {
      this.#clearResults();
      return;
    }

    this.#timer = window.setTimeout(() => this.#search(query), 180);
  };

  #onFocus = () => {
    if (this.input.value.trim().length >= this.minChars && this.results.childElementCount > 0) {
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }
  };

  #onDocumentPointerDown = (event) => {
    if (!this.contains(event.target)) {
      this.#hideResults();
    }
  };

  #onKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#hideResults();
      this.input.focus();
      return;
    }

    if (this.results.hidden || this.#items.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.#select(this.#selectedIndex < this.#items.length - 1 ? this.#selectedIndex + 1 : 0);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.#select(this.#selectedIndex > 0 ? this.#selectedIndex - 1 : this.#items.length - 1);
      return;
    }

    if (event.key === 'Enter' && this.#selectedIndex >= 0) {
      const link = this.#items[this.#selectedIndex]?.querySelector('a');
      if (link) {
        event.preventDefault();
        link.click();
      }
    }
  };

  async #search(query) {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    const url = new URL(Theme.routes.predictive_search_url, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', String(this.limit));
    url.searchParams.set('resources[limit_scope]', 'each');

    try {
      const html = await sectionRenderer.getSectionHTML('predictive-search', false, url, signal);
      if (signal.aborted || query !== this.input.value.trim()) return;

      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const source = documentFragment.querySelector('#predictive-search-results');

      if (!source) {
        this.#clearResults();
        return;
      }

      this.#renderResults(source, query);
    } catch (error) {
      if (signal.aborted) return;
      this.#clearResults();
      console.error('Goddess Winter predictive search failed', error);
    }
  }

  #renderResults(source, query) {
    const productSection = source.querySelector('#predictive-search-products');
    const noResults = source.querySelector('.predictive-search-results__no-results');

    this.results.replaceChildren();

    const shell = document.createElement('div');
    shell.className = 'predictive-search-dropdown';
    shell.id = `${this.results.id}-listbox`;
    shell.setAttribute('role', 'listbox');
    const listboxLabel = source.getAttribute('aria-label');
    if (listboxLabel) {
      shell.setAttribute('aria-label', listboxLabel);
    }

    const inner = document.createElement('div');
    inner.className = 'predictive-search-results__inner';
    inner.setAttribute('data-search-results', '');

    if (productSection) {
      const productSectionClone = productSection.cloneNode(true);
      const productTitle = productSectionClone.querySelector('.predictive-search-results__title');
      const productList = productSectionClone.querySelector('.predictive-search-results__wrapper-products');

      if (productTitle && productList) {
        const productTitleId = `${this.results.id}-products-label`;
        productTitle.id = productTitleId;
        productList.setAttribute('role', 'group');
        productList.setAttribute('aria-labelledby', productTitleId);
      }

      inner.append(productSectionClone);
      shell.append(inner);
      this.results.append(shell);

      const viewAll = document.createElement('a');
      const searchUrl = new URL(Theme.routes.search_url, window.location.origin);
      searchUrl.searchParams.set('type', 'product');
      searchUrl.searchParams.set('q', query);
      viewAll.href = searchUrl.toString();
      viewAll.className = 'gw-header-search__view-all';
      viewAll.textContent = `View all results for “${query}”`;
      this.results.append(viewAll);
    } else if (noResults) {
      inner.append(noResults.cloneNode(true));
      shell.append(inner);
      this.results.append(shell);
    } else {
      this.#clearResults();
      return;
    }

    this.#items = Array.from(this.results.querySelectorAll('.predictive-search-results__card--product'));
    this.#items.forEach((item, itemIndex) => {
      item.id = `${this.results.id}-option-${itemIndex}`;
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', 'false');
      item.classList.remove('keyboard-focus');

      const link = item.querySelector('a');
      if (link) {
        link.tabIndex = -1;
      }
    });
    this.#selectedIndex = -1;
    this.input.removeAttribute('aria-activedescendant');
    this.results.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  #select(index) {
    this.#items.forEach((item, itemIndex) => {
      const selected = itemIndex === index;
      item.setAttribute('aria-selected', selected ? 'true' : 'false');
      item.classList.toggle('keyboard-focus', selected);
    });

    this.#selectedIndex = index;
    const selectedItem = this.#items[index];
    if (selectedItem?.id) {
      this.input.setAttribute('aria-activedescendant', selectedItem.id);
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }
    selectedItem?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  #hideResults() {
    this.results.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.#items.forEach((item) => {
      item.setAttribute('aria-selected', 'false');
      item.classList.remove('keyboard-focus');
    });
    this.#selectedIndex = -1;
  }

  #clearResults() {
    this.#controller?.abort();
    this.results.replaceChildren();
    this.results.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.#items = [];
    this.#selectedIndex = -1;
  }
}

if (!customElements.get('gw-header-predictive-search')) {
  customElements.define('gw-header-predictive-search', GWHeaderPredictiveSearch);
}